import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { iif, Observable, switchMap, throwError } from 'rxjs'
import { catchError, map, take, tap } from 'rxjs/operators'
import { MyCiceroWebApiService } from '../mycicero-web-api/my-cicero-web-api.service'
import { InitialConfigState } from './store/reducers/initial-config.reducer'
import { selectInitialConfigState, selectLoaded } from './store/selectors/initial-config.selectors'
import { InitialConfigActions } from './store/actions/initial-config.actions'
import { GetInitialConfigurationMapper } from './get-initial-configuration-mapper'
import { DomSanitizer } from '@angular/platform-browser'

@Injectable({
  providedIn: 'root',
})
export class InitialConfigService {
  /**
   * Creates an instance of InitialConfigService.
   * @param myciceroApi Service for API calls related to initial configuration.
   * @param store Store for managing application state.
   * @param sanitizer DOMSanitizer for external links
   */
  constructor(
    private readonly myciceroApi: MyCiceroWebApiService,
    private readonly store: Store,
    private readonly sanitizer: DomSanitizer,
  ) {}

  /**
   * Loads the initial configuration from the store if already loaded, otherwise fetches it from the API and updates the store.
   * @returns An observable with the initial configuration state.
   */
  getInitialConfiguration(): Observable<InitialConfigState> {
    return this.store.select(selectLoaded).pipe(
      take(1),
      switchMap((loaded) =>
        iif(
          () => loaded,
          this.store.select(selectInitialConfigState).pipe(take(1)),
          this.myciceroApi.getInitialConfiguration().pipe(
            map((conf) => GetInitialConfigurationMapper(conf, this.sanitizer)),
            catchError((error) => {
              console.error(error)
              this.store.dispatch(InitialConfigActions.initialConfigError())
              return throwError(() => {
                return new Error(error)
              })
            }),
            tap((initialConfig) =>
              this.store.dispatch(InitialConfigActions.initialConfigLoaded({ initialConfig: initialConfig })),
            ),
          ),
        ),
      ),
    )
  }
}
